// Agendar no My Planner com um horário diferente em cada dia da semana (0.47).
// Ex.: fichário "Doutorado" na segunda às 14:00, terça às 10:00 e quinta às 16:00.
// Cria uma tarefa semanal para cada dia escolhido, todas ligadas ao fichário ou à matéria (tocar na tarefa abre a aba Estudos).
import { useEffect, useState } from 'react';
import { Alert, Pressable, ScrollView, Text, View } from 'react-native';
import type { Link } from '../store/types';
import { getDoc, setDoc } from '../store/store';
import { newTask, saveTask } from '../store/planner';
import { Btn, Chip, Sheet } from './ui';
import { C, alpha } from './theme';
import { PickerModal } from './Pickers';
import { DL, horaDate } from './bemestar/comum';
import { hm, today } from '../lib/date';
import { N_, t } from '../lib/i18n';

const ORDEM = [1, 2, 3, 4, 5, 6, 0];
const NOMES = [N_('Domingo'), N_('Segunda'), N_('Terça'), N_('Quarta'), N_('Quinta'), N_('Sexta'), N_('Sábado')];

export type Agendar = { titulo: string; emoji: string; link: NonNullable<Link>; notas?: string };

export function AgendarSemana({ alvo, cor, onClose, onUmaVez }: { alvo: Agendar | null; cor: string; onClose: () => void; onUmaVez?: () => void }) {
  const [horas, setHoras] = useState<Record<number, string>>({});
  const [dur, setDur] = useState(50);
  const [picker, setPicker] = useState<number | null>(null);
  // começa com os horários que já existem para este fichário/matéria (se houver)
  useEffect(() => {
    if (!alvo) return;
    const h: Record<number, string> = {};
    for (const x of getDoc('planner').tasks) {
      if (x.link?.aba === alvo.link.aba && x.link?.id === alvo.link.id && x.recur.type === 'weekly' && x.time)
        for (const d of x.recur.days ?? []) h[d] = x.time;
    }
    setHoras(h); setDur(50);
  }, [alvo]);
  if (!alvo) return null;
  const dias = ORDEM.filter(d => horas[d]);
  const existentes = () => getDoc('planner').tasks.filter(x => x.link?.aba === alvo.link.aba && x.link?.id === alvo.link.id && x.recur.type === 'weekly');

  const criar = (substituir: boolean) => {
    if (substituir) { const ids = new Set(existentes().map(x => x.id)); setDoc('planner', d => ({ tasks: d.tasks.filter(x => !ids.has(x.id)) })); }
    for (const d of dias) {
      saveTask(newTask({ title: alvo.titulo, emoji: alvo.emoji, emojiManual: true, area: 'estudos', start: today(), time: horas[d], duration: dur, energy: 3,
        recur: { type: 'weekly', days: [d] }, link: alvo.link, notes: alvo.notas ?? '' }));
    }
    onClose();
    Alert.alert(t('Agendado no My Planner'), dias.map(d => `${t(NOMES[d])} ${horas[d]}`).join(' · '));
  };
  const salvar = () => {
    if (!dias.length) return Alert.alert(t('Escolha pelo menos um dia.'));
    if (existentes().length) Alert.alert(t('Já há horários deste item no My Planner'), t('Quer trocar pelos novos ou manter os dois?'), [
      { text: t('Cancelar'), style: 'cancel' }, { text: t('Manter ambos'), onPress: () => criar(false) }, { text: t('Substituir'), onPress: () => criar(true) }]);
    else criar(false);
  };

  return (
    <Sheet visible={!!alvo} onClose={onClose} title={t('Agendar no My Planner')}
      right={<Pressable onPress={salvar}><Text style={{ color: cor, fontWeight: '800' }}>{t('Salvar')}</Text></Pressable>}>
      <ScrollView contentContainerStyle={{ padding: 16, gap: 10, paddingBottom: 40 }}>
        <Text style={{ color: C.text, fontSize: 18, fontWeight: '800' }}>{alvo.emoji} {alvo.titulo}</Text>
        <Text style={{ color: C.sub, fontSize: 13 }}>{t('Marque os dias em que você estuda e o horário de cada dia. Cada dia vira uma tarefa que se repete toda semana, ligada a este item.')}</Text>
        {ORDEM.map(d => {
          const on = !!horas[d];
          const alternar = () => setHoras(h => { const n = { ...h }; if (on) delete n[d]; else n[d] = Object.values(h)[0] ?? '14:00'; return n; });
          return (
            <Pressable key={d} onPress={alternar} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: on ? alpha(cor, 0.14) : C.card, borderRadius: 14, padding: 10 }}>
              <View style={{ width: 38, height: 38, borderRadius: 19, alignItems: 'center', justifyContent: 'center', backgroundColor: on ? cor : C.card2 }}>
                <Text style={{ color: on ? '#fff' : C.sub, fontWeight: '700', fontSize: 12 }} numberOfLines={1} adjustsFontSizeToFit>{DL(d)}</Text>
              </View>
              <Text style={{ color: on ? C.text : C.sub, flex: 1, fontWeight: on ? '700' : '400' }}>{t(NOMES[d])}</Text>
              {on && (
                <Pressable onPress={() => setPicker(d)} style={{ backgroundColor: C.card2, borderRadius: 10, paddingHorizontal: 14, paddingVertical: 8 }}>
                  <Text style={{ color: C.text, fontSize: 16, fontWeight: '700' }}>🕘 {horas[d]}</Text>
                </Pressable>
              )}
            </Pressable>
          );
        })}
        <Text style={{ color: C.sub, fontSize: 12, marginTop: 6 }}>{t('Duração de cada sessão')}</Text>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
          {[30, 50, 60, 90, 120].map(n => <Chip key={n} small cor={cor} on={dur === n} label={n < 60 || n % 60 ? `${n} min` : `${n / 60} h`} onPress={() => setDur(n)} />)}
        </View>
        <Btn cor={cor} label={t('Salvar no My Planner') + (dias.length ? ` (${dias.length})` : '')} onPress={salvar} style={{ marginTop: 8 }} />
        {onUmaVez && <Btn cor={cor} outline label={'📅 ' + t('Só uma vez (abrir a ficha da tarefa)')} onPress={() => { onClose(); setTimeout(onUmaVez, 300); }} />}
      </ScrollView>
      {picker != null && <PickerModal mode="time" cor={cor} value={horaDate(horas[picker] ?? '14:00')} onClose={() => setPicker(null)}
        onPick={v => { const d = picker; setPicker(null); setHoras(h => ({ ...h, [d]: hm(v) })); }} />}
    </Sheet>
  );
}
